import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Bonus requests queue. Managers see the bonuses they've filed +
// their status. Owners see every pending request and have approve/
// deny buttons. CJ feedback 2026-05-11.
export function PayrollBonusesView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const isOwner = /^(owner|admin|administrator)$/i.test(currentUser?.role || '');
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [draft, setDraft] = useState({ user_id: '', amount: '', reason: '' });
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api('GET', '/api/admin/payroll/bonus-requests', null, headers),
        api('GET', '/api/users', null, headers).catch(() => []),
      ]);
      setRows(Array.isArray(r?.bonuses) ? r.bonuses : []);
      setUsers(Array.isArray(u) ? u : (Array.isArray(u?.users) ? u.users : []));
    } catch (_) { setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submitNew() {
    if (!draft.user_id || !Number(draft.amount)) {
      setSavingMsg('Pick an employee and enter an amount.'); return;
    }
    setSavingMsg('Saving…');
    try {
      await api('POST', '/api/admin/payroll/bonus-requests', {
        user_id: Number(draft.user_id),
        amount: Number(draft.amount),
        reason: draft.reason || null,
      }, headers);
      setShowNew(false);
      setDraft({ user_id: '', amount: '', reason: '' });
      setSavingMsg('');
      await load();
    } catch (e) { setSavingMsg(`Failed: ${e.message || e}`); }
  }
  async function decide(id, decision) {
    let note = null;
    if (decision === 'denied') {
      note = window.prompt('Reason (optional, shown to the requesting manager):') || null;
    }
    try {
      await api('POST', `/api/admin/payroll/bonus-requests/${id}/decide`,
        { decision, note }, headers);
      await load();
    } catch (e) { alert(e.message || String(e)); }
  }

  function fmtWhen(s) {
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
    catch { return s; }
  }
  const statusColor = {
    pending:  { bg: '#fef9ee', fg: '#8e6c33', bd: '#f5e6c8' },
    approved: { bg: '#e6f4ea', fg: '#1e7d3a', bd: '#c8e2c8' },
    denied:   { bg: '#fef2f2', fg: '#a13030', bd: '#fecaca' },
  };

  return (
    <div className="space-y-3">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Bonus requests</div>
            <div className="text-xs text-gray-500">
              {isOwner
                ? 'Approve or deny manager bonus requests. Approved bonuses appear on the next payroll run for that pay period.'
                : 'Request a bonus for a staff member. Owner reviews and approves. Approved bonuses are added to the next payroll run.'}
            </div>
          </div>
          <button onClick={() => setShowNew(true)}
            style={{ padding: '8px 14px', background: 'var(--gold)', color: '#fff',
                      border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            + New request
          </button>
        </div>
      </div>

      {loading ? <div className="card text-sm text-gray-500">Loading…</div> : rows.length === 0 ? (
        <div className="card text-sm text-gray-500" style={{ fontStyle: 'italic' }}>
          No bonus requests yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const c = statusColor[r.status] || statusColor.pending;
            return (
              <div key={r.id} className="card" style={{ borderLeft: `4px solid ${c.fg}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {r.user_name} — ${Number(r.amount).toFixed(2)}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      Pay period {fmtWhen(r.week_start)} · Filed by {r.requested_by_name || '—'} on {fmtWhen(r.created_at)}
                    </div>
                    {r.reason && <div style={{ fontSize: 13, color: '#444', marginTop: 6 }}>{r.reason}</div>}
                    {r.decision_note && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4, fontStyle: 'italic' }}>
                        Decision note: {r.decision_note}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 99, fontWeight: 700,
                                      background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
                      {r.status.toUpperCase()}
                    </span>
                    {isOwner && r.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => decide(r.id, 'approved')}
                          style={{ padding: '4px 12px', border: 0, background: '#1e7d3a', color: '#fff',
                                    fontWeight: 600, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Approve</button>
                        <button onClick={() => decide(r.id, 'denied')}
                          style={{ padding: '4px 12px', border: '1px solid #fecaca', background: '#fef2f2', color: '#a13030',
                                    fontWeight: 600, borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Deny</button>
                      </div>
                    )}
                    {r.decided_at && (
                      <div style={{ fontSize: 11, color: '#888' }}>
                        by {r.decided_by_name || '—'} on {fmtWhen(r.decided_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,29,0.45)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => setShowNew(false)}>
          <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 480, width: '100%' }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>New bonus request</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              The bonus will apply to the current open pay period.
            </div>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Employee</label>
            <select value={draft.user_id}
              onChange={(e) => setDraft((d) => ({ ...d, user_id: e.target.value }))}
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, marginBottom: 10 }}>
              <option value="">— Pick an employee —</option>
              {users.filter((u) => (u.status || 'active') === 'active')
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Amount ($)</label>
            <input type="number" min="0" step="0.01" value={draft.amount}
              onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
              placeholder="Enter amount"
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, marginBottom: 10, boxSizing: 'border-box' }} />
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Reason</label>
            <textarea value={draft.reason} rows={3}
              onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
              placeholder="e.g. Covered a closing shift on short notice"
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13,
                        fontFamily: 'inherit', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12, alignItems: 'center' }}>
              {savingMsg && <span style={{ fontSize: 12, color: savingMsg.startsWith('Failed') ? '#b00020' : '#666' }}>{savingMsg}</span>}
              <button onClick={() => setShowNew(false)}
                style={{ padding: '6px 14px', border: '1px solid #d0d4d9', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={submitNew}
                style={{ padding: '6px 16px', border: 0, background: 'var(--gold)', color: '#fff',
                          fontWeight: 600, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
